// ── SALES TAX — three views (design handoff v3, 2026-10-16) ──────────────
// Built literally from design_handoffs/design_handoff_sales_tax_overview
// (HANDOFF.md + screens/1-returns-due, 2-filed, 3-detail-by-month).
//
// The page used to be a period × state grid, so every cell had to exist
// whether or not anything happened there — dashes, three Colorado rows a
// month, zero returns in fuchsia, future months rendered as due. Cimon,
// 2026-10-16: "this page is a mess. it's trying to do lots of things."
// It was four tools in one: a to-do list, a ledger, a history, a rule
// explainer. Now one question per view, switched under the heading and
// persisted in the URL (?view=due|filed|detail):
//
//   Returns due   What do I owe, to whom, by when?   (landing)
//   Filed         What was filed and paid; does it reconcile?
//   Detail        The figures behind each return, for the accountants.
//
// Nothing about the structure changed: all three views read the same
// stored filings, stamps and FIN snapshots. The filing modal is untouched.
// Pure builders live in utils.js (buildSalesTaxReturnsDue,
// buildSalesTaxFiledRows); this file only renders what they return.
//
// Color budget (handoff §6): green dot = complete, only as a dot. Fuchsia =
// overdue, short-paid, and the "Filed Mon D" of a half-recorded filing.
// Nothing else is colored. No dashes: an empty figure is $0.00, an absent
// record is a word.
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAppContext } from './AppContext';
import SalesTaxFilingModal from './SalesTaxFilingModal';
import {
  fmtMoney, buildSalesTaxReport, buildSalesTaxReturnsDue, buildSalesTaxFiledRows,
  salesTaxReturnFilterOptions, salesTaxRowReturns, salesTaxFilingsFor, sumSalesTaxFilings,
  salesTaxPeriodKey, salesTaxPeriodLabel, salesTaxPeriodEndDate, salesTaxPeriodStartDate, salesTaxPeriodState,
  salesTaxDueDate, salesTaxStateRuleFor, salesTaxShortDate, salesTaxReportCsv, salesTaxReturnOwedForPeriod,
} from '../utils';
import { INK_1, INK_2, INK_3, INK_4, LINE_CONTAINER, LINE_ROW, SURFACE, ZONE_FUCHSIA, ZONE_GREEN, TABULAR } from '../lib/designTokens';

const VIEWS = [['due', 'Returns due'], ['filed', 'Filed'], ['detail', 'Detail by month']];
const DUE_GRID = '96px minmax(0,1fr) 150px 140px 180px';
// Filed grid (2026-10-16 layout fix): Return · Period gets the room; every
// cell is min-width 0 so nothing can spill into its neighbour.
const FILED_GRID = '14px 80px minmax(310px,1fr) 120px 130px 150px 70px minmax(180px,220px) 50px';

// ── URL: the chosen view rides the hash query so a link to the Filed view
// can be sent to Dan. HashRouter owns the path; we only touch the query.
function readViewFromHash() {
  try {
    const q = String(window.location.hash || '').split('?')[1] || '';
    const v = new URLSearchParams(q).get('view');
    return VIEWS.some(function(x) { return x[0] === v; }) ? v : 'due';
  } catch (e) { return 'due'; }
}
function writeViewToHash(v) {
  try {
    const h = String(window.location.hash || '');
    const base = h.split('?')[0];
    const params = new URLSearchParams(h.split('?')[1] || '');
    params.set('view', v);
    window.history.replaceState(null, '', base + '?' + params.toString());
  } catch (e) { /* jsdom / blocked history — the view still switches */ }
}
function todayIso() { return new Date().toISOString().slice(0, 10); }
function nextMonthKey(iso) {
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + 1, 1);
  return salesTaxPeriodKey(d.toISOString().slice(0, 10), 'month');
}
// No dashes anywhere on the tab (handoff §6): a state with no rate on
// file prints a blank rate cell.
function ratePct(rate) {
  if (rate == null) return 'mixed';
  if (!rate) return '';
  return String(Number((rate * 100).toFixed(4))) + '%';
}
function downloadCsv(filename, csvText) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(function() { URL.revokeObjectURL(url); }, 0);
}

// ── Styles (handoff §7 tokens).
const S = {
  lbl: { fontSize: 10, letterSpacing: '0.07em', textTransform: 'uppercase', color: INK_3, fontWeight: 500 },
  body: { fontSize: 14, lineHeight: 1.45, color: INK_1, fontFeatureSettings: TABULAR },
  mute: { color: INK_3 }, ink2: { color: INK_2 }, second: { fontSize: 13, color: INK_2 },
  num: { textAlign: 'right', fontFeatureSettings: TABULAR },
  seg: function(on) {
    return {
      display: 'inline-flex', alignItems: 'center', height: 36, padding: '0 16px', borderRadius: 999, background: SURFACE,
      border: '1px solid ' + (on ? INK_1 : LINE_CONTAINER), color: on ? INK_1 : INK_2, fontWeight: on ? 500 : 400,
      fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
    };
  },
  pill: function(solid) {
    return {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 34, padding: '0 16px', borderRadius: 999, background: SURFACE,
      border: '1px solid ' + (solid ? INK_1 : INK_4), color: solid ? INK_1 : INK_2, fontWeight: solid ? 500 : 400,
      fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
    };
  },
  select: { height: 34, border: '1px solid ' + LINE_CONTAINER, borderRadius: 999, padding: '0 12px', background: SURFACE, font: 'inherit', fontSize: 13, color: INK_1 },
  lnk: { color: INK_1, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: INK_4, cursor: 'pointer', background: 'none', border: 0, padding: 0, font: 'inherit' },
  lnk2: { color: INK_2, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: INK_4, cursor: 'pointer', background: 'none', border: 0, padding: 0, font: 'inherit', fontSize: 13 },
  row: function(grid, extra) {
    return Object.assign({ display: 'grid', gridTemplateColumns: grid, gap: '0 16px', alignItems: 'center', minHeight: 52, padding: '10px 0', borderBottom: '1px solid ' + LINE_ROW }, extra || {});
  },
  mono: { fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 12.5, color: INK_2 },
};
function Dot(p) {
  const filled = p.state === 'filed';
  return <span data-testid="tax-dot" data-state={p.state} style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', flex: 'none', boxSizing: 'border-box', background: filled ? ZONE_GREEN : 'transparent', border: filled ? 'none' : '1px solid ' + INK_3 }} />;
}
function Bold(p) { return <span style={{ color: p.alert ? ZONE_FUCHSIA : INK_1, fontWeight: 500 }}>{p.children}</span>; }

export default function SalesTaxDueSection(props) {
  const ctx = useAppContext();
  const invoices           = ctx.invoices || [];
  const items              = ctx.items || [];
  const paymentAllocations = ctx.paymentAllocations || [];
  const clientPayments     = ctx.clientPayments || [];
  const projects           = ctx.projects || [];
  const stateTaxRecords    = ctx.stateTaxRecords || [];
  const finalInvoiceItems  = ctx.finalInvoiceItems || [];
  const purchaseOrders     = ctx.purchaseOrders || [];
  const vendorPayments     = ctx.vendorPayments || [];
  const vendorPaymentItems = ctx.vendorPaymentItems || [];
  const vendorCreditLedger = ctx.vendorCreditLedger || [];
  const salesTaxFilings    = ctx.salesTaxFilings || [];
  const salesTaxCodes      = ctx.salesTaxCodes || [];
  const salesTaxFilingItems = ctx.salesTaxFilingItems || [];
  const salesTaxFilingFiles = ctx.salesTaxFilingFiles || [];
  const canRecord = !!ctx.commitSalesTaxFilings;
  const today = (props && props.today) || todayIso();   // injectable for tests

  const [view, setViewState] = useState(readViewFromHash);
  function setView(v) { setViewState(v); writeViewToHash(v); }
  const [year, setYear] = useState(String(today.slice(0, 4)));
  const [stateFilter, setStateFilter] = useState('');
  const [returnFilter, setReturnFilter] = useState('');
  const [modal, setModal] = useState(null);
  const queueRef = useRef([]);   // further returns to record after the open modal closes ("Record 2 zero returns")

  const base = { invoices, items, paymentAllocations, clientPayments, projects, stateTaxRecords, finalInvoiceItems, purchaseOrders, vendorPayments, vendorPaymentItems, vendorCreditLedger, salesTaxCodes, today };
  const deps = [invoices, items, paymentAllocations, clientPayments, projects, stateTaxRecords, finalInvoiceItems, purchaseOrders, vendorPayments, vendorPaymentItems, vendorCreditLedger, salesTaxCodes, today];

  // Every period, every year, through next month — the Returns-due list
  // crosses the year boundary (a December return is due in January) and
  // names next month's returns before they open.
  const reportAll = useMemo(function() {
    return buildSalesTaxReport(Object.assign({}, base, { grouping: 'month', year: '', spineThrough: nextMonthKey(today) }));
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  const filingRefs = { salesTaxFilings, salesTaxFilingItems, salesTaxFilingFiles, stateTaxRecords, salesTaxCodes };
  const dueModel = useMemo(function() {
    return buildSalesTaxReturnsDue(reportAll, filingRefs, { today: today, horizonDays: 45 });
  }, [reportAll, salesTaxFilings, salesTaxFilingItems, salesTaxFilingFiles, stateTaxRecords, salesTaxCodes, today]);

  // The year's report for the Detail view and the CSV.
  const reportYear = useMemo(function() {
    return buildSalesTaxReport(Object.assign({}, base, { grouping: 'month', year: year }));
  }, deps.concat([year])); // eslint-disable-line react-hooks/exhaustive-deps
  const years = useMemo(function() {
    const seen = {}; seen[today.slice(0, 4)] = true;
    reportAll.periods.forEach(function(p) { seen[p.key.slice(0, 4)] = true; });
    salesTaxFilings.forEach(function(f) { const y = String(f.periodKey || '').slice(0, 4); if (y) seen[y] = true; });
    return Object.keys(seen).sort().reverse();
  }, [reportAll, salesTaxFilings, today]);
  const stateOptions = useMemo(function() {
    const seen = {};
    reportAll.periods.forEach(function(p) { p.states.forEach(function(st) { seen[st.stateCode] = st.stateName; }); });
    return Object.keys(seen).sort().map(function(code) { return { code: code, name: seen[code] }; });
  }, [reportAll]);
  const returnOptions = useMemo(function() { return salesTaxReturnFilterOptions(stateTaxRecords, salesTaxCodes); }, [stateTaxRecords, salesTaxCodes]);
  const filed = useMemo(function() {
    return buildSalesTaxFiledRows(salesTaxFilings, filingRefs, { year: year, returnFilter: returnFilter });
  }, [salesTaxFilings, salesTaxFilingItems, salesTaxFilingFiles, stateTaxRecords, salesTaxCodes, year, returnFilter]);

  // ── Modal plumbing. The modal takes [{period, st}] + ret + existing; a
  // folded quarterly state files on ITS period key, not the host month's.
  function openModal(period, st, existing, initialStep, ret) {
    const returns = st.returns || [];
    let per = period;
    if (st.periodKey) per = Object.assign({}, period, { key: st.periodKey, label: st.periodLabel + ' (' + st.periodRange + ')' });
    const chosen = ret || (existing ? returns.find(function(r) { return r.key === String(existing.returnKey || '').toUpperCase(); }) : null) || returns[0] || null;
    setModal({ periods: [{ period: per, st: st }], existing: existing || null, ret: chosen && returns.length ? chosen : null, stateCode: st.stateCode, stateName: st.stateName, initialStep: initialStep || (existing ? 3 : 1), grouping: st.filingGrouping || 'month' });
  }
  function closeModal() {
    const next = queueRef.current.shift();
    if (next) { openModal(next.period, next.st, null, next.step, next.ret); return; }
    setModal(null);
  }
  // Locate the report row behind a stored filing (for Edit / Complete record).
  function rowForFiling(f) {
    for (let i = 0; i < reportAll.periods.length; i++) {
      const p = reportAll.periods[i];
      const st = p.states.find(function(s) { return s.stateCode === f.stateCode && ((s.periodKey || p.key) === f.periodKey); });
      if (st) return { period: p, st: st };
    }
    // A filing on a period the report no longer spans: a bare row so the
    // record still opens.
    return {
      period: { key: f.periodKey, label: salesTaxPeriodLabel(f.periodKey) },
      st: { stateCode: f.stateCode, stateName: f.stateCode, tax: Number(f.taxDueSnapshot) || 0, hasActivity: (Number(f.taxDueSnapshot) || 0) > 0, projects: [], returns: [], registered: true, filingGrouping: f.grouping || 'month' },
    };
  }
  function editFiling(f) { const r = rowForFiling(f); openModal(r.period, r.st, f, 3); }
  async function openFiles(files) {
    if (!ctx.salesTaxFilingFileUrl || !files.length) return;
    for (let i = 0; i < files.length; i++) {
      const url = await ctx.salesTaxFilingFileUrl(files[i].storagePath);
      if (url) window.open(url, '_blank', 'noopener');
    }
  }
  function openItems(ids) {
    if (!ids || !ids.length) return;
    window.location.hash = '#/items?ids=' + ids.join(',');
  }
  function exportCsv() {
    let v = reportYear;
    if (stateFilter) {
      v = Object.assign({}, reportYear, { periods: reportYear.periods.map(function(p) { return Object.assign({}, p, { states: p.states.filter(function(st) { return st.stateCode === stateFilter; }) }); }).filter(function(p) { return p.states.length; }) });
    }
    downloadCsv('sales-tax-' + year + (stateFilter ? '-' + stateFilter : '') + '-monthly.csv', salesTaxReportCsv(v, salesTaxFilings));
  }

  // ── Header: one sentence + the segmented control.
  function sentence() {
    const s = dueModel.sentence;
    if (view === 'filed') {
      return <p style={{ margin: 0, color: INK_2 }}><Bold>{fmtMoney(filed.totals.paid)}</Bold> remitted in {year} across {filed.totals.filings} filing{filed.totals.filings === 1 ? '' : 's'}. Every line is a stored record; amounts never recompute.</p>;
    }
    if (view !== 'due' || !s) return null;
    if (s.kind === 'overdue') return <p style={{ margin: 0, color: INK_2 }} data-testid="tax-sentence"><Bold alert>{s.count} return{s.count === 1 ? '' : 's'} overdue</Bold> · {fmtMoney(s.unpaid)} unpaid.</p>;
    if (s.kind === 'ready') return <p style={{ margin: 0, color: INK_2 }} data-testid="tax-sentence"><Bold>{s.count} return{s.count === 1 ? '' : 's'} ready to file</Bold>{s.nextDue ? <span> · next due <Bold>{salesTaxShortDate(s.nextDue)}</Bold></span> : null} · {fmtMoney(s.remit)} to remit.</p>;
    return (
      <p style={{ margin: 0, color: INK_2 }} data-testid="tax-sentence">
        Nothing is due today.{s.nextDue ? <span> The next return is due <Bold>{salesTaxShortDate(s.nextDue)}</Bold></span> : null}
        {s.count ? <span> · {s.count} return{s.count === 1 ? '' : 's'} and <Bold>{fmtMoney(s.amount)}</Bold> fall due in the next {s.horizonDays} days.</span> : (s.nextDue ? '.' : '')}
      </p>
    );
  }

  // ── View 1: Returns due.
  function dueLineCopy(ln) {
    if (ln.kind === 'short') return null;
    if (ln.periodState === 'not_started') return null;
    const unit = ln.grouping === 'quarter' ? 'quarter' : (ln.grouping === 'year' ? 'year' : 'month');
    if (ln.zero) return <div style={S.second}>No {ln.stateName} sales this {unit}</div>;
    if (!ln.deliveryBasis || !ln.counts || !ln.counts.total) return null;
    const c = ln.counts;
    const parts = [];
    parts.push(<span key="d">Delivery basis · {c.delivered.length} item{c.delivered.length === 1 ? '' : 's'} delivered to {ln.stateCode} this {unit}</span>);
    const link = function(key, ids, text) {
      return <span key={key}> · <button type="button" onClick={function() { openItems(ids); }} style={S.lnk2} data-testid={'due-items-' + key}>{ids.length} item{ids.length === 1 ? '' : 's'} {text}</button></span>;
    };
    if (c.planned.length) parts.push(link('planned', c.planned, 'on a planned-install date'));
    if (c.fallback.length) parts.push(link('fallback', c.fallback, 'on a paid date (no delivery date)'));
    if (c.firstReturn.length) parts.push(link('firstreturn', c.firstReturn, 'pre-registration, moved to first return'));
    return <div style={S.second}>{parts}</div>;
  }
  function dueLine(ln, muted) {
    const inkA = muted ? INK_3 : INK_1;
    const key = (ln.kind === 'short' ? 'short:' : 'due:') + ln.stateCode + ':' + ln.periodKey + ':' + (ln.ret && ln.ret.key ? ln.ret.key : 'STATE');
    const open = ln.periodState === 'open' || ln.periodState === 'not_started';
    let dueCell;
    if (ln.kind === 'short') dueCell = <span style={{ color: ZONE_FUCHSIA, fontWeight: 500 }}>Short {fmtMoney(ln.shortBy)}</span>;
    else if (ln.periodState === 'overdue') dueCell = <span style={{ color: ZONE_FUCHSIA, fontWeight: 500 }}>Overdue{ln.dueDate ? ' · was ' + salesTaxShortDate(ln.dueDate) : ''}</span>;
    else dueCell = <span style={{ fontWeight: 500, color: inkA }}>{ln.dueDate ? salesTaxShortDate(ln.dueDate) : ''}</span>;
    let status = '';
    if (ln.kind !== 'short') {
      if (ln.periodState === 'open') status = 'period closes ' + salesTaxShortDate(salesTaxPeriodEndDate(ln.periodKey));
      else if (ln.periodState === 'not_started') status = 'period opens ' + salesTaxShortDate(salesTaxPeriodStartDate(ln.periodKey));
      else if (ln.periodState === 'closed') status = 'ready';
    }
    if (muted) status = 'accumulating';
    let action = null;
    if (!muted && canRecord) {
      const solid = ln.periodState === 'closed' || ln.periodState === 'overdue';
      if (ln.kind === 'short') action = <button type="button" onClick={function() { editFiling(ln.filing); }} style={S.lnk2}>Edit</button>;
      else if (ln.action === 'file') action = <button type="button" onClick={function() { openModal(ln.period, ln.st, null, 1, ln.ret); }} style={S.pill(solid)} data-testid={'file-record-' + key}>File &amp; record</button>;
      else if (ln.action === 'zero') {
        const rets = ln.rets || [ln.ret];
        action = (
          <button type="button" style={S.pill(solid)} data-testid={'zero-return-' + key}
                  onClick={function() {
                    queueRef.current = rets.slice(1).map(function(r) { return { period: ln.period, st: ln.st, step: 3, ret: r }; });
                    openModal(ln.period, ln.st, null, 3, rets[0]);
                  }}>
            {rets.length > 1 ? 'Record ' + rets.length + ' zero returns' : 'Record zero return'}
          </button>
        );
      }
    }
    return (
      <div key={key} style={S.row(DUE_GRID)} data-testid={'due-line-' + key}>
        <div>{dueCell}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: inkA }}>
            {ln.label}
            {(ln.extraLabels || []).map(function(x, i) { return <span key={i} style={S.mute}> + {x}</span>; })}
            <span style={S.mute}> · {ln.periodLabel}</span>
            {muted && ln.grouping === 'quarter' && <span style={S.mute}> · files quarterly</span>}
          </div>
          {!muted && dueLineCopy(ln)}
        </div>
        <div style={Object.assign({}, S.num, { color: inkA })}>{fmtMoney(ln.tax)}{(open || muted) && ln.kind !== 'short' ? <span style={S.mute}> so far</span> : null}</div>
        <div style={S.mute}>{status}</div>
        <div style={{ textAlign: 'right' }}>{action}</div>
      </div>
    );
  }
  function viewDue() {
    const m = dueModel;
    return (
      <div data-testid="tax-view-due">
        {m.attention.length > 0 && (
          <div>
            <div style={Object.assign({}, S.lbl, { padding: '8px 0', borderBottom: '1px solid ' + LINE_CONTAINER })}>Needs attention</div>
            {m.attention.map(function(a) {
              return (
                <div key={a.filing.id} style={S.row(DUE_GRID)} data-testid={'attention-' + a.filing.id}>
                  <div style={{ color: ZONE_FUCHSIA, fontWeight: 500 }}>Filed {salesTaxShortDate(a.filedDate)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div>{a.returnLabel} <span style={S.mute}>· {a.periodLabel}</span></div>
                    <div style={S.second}>{a.text}</div>
                  </div>
                  <div style={S.num}>{fmtMoney(a.amountPaid)} <span style={S.mute}>paid</span></div>
                  <div style={S.mute}>half recorded</div>
                  <div style={{ textAlign: 'right' }}>{canRecord && <button type="button" onClick={function() { editFiling(a.filing); }} style={S.pill(true)}>Complete record</button>}</div>
                </div>
              );
            })}
          </div>
        )}
        <div style={Object.assign({}, S.lbl, { padding: (m.attention.length ? 28 : 8) + 'px 0 8px', borderBottom: '1px solid ' + LINE_CONTAINER, display: 'grid', gridTemplateColumns: DUE_GRID, gap: '0 16px' })}>
          <span>Due</span><span>Return · period</span><span style={S.num}>Tax to remit</span><span>Status</span><span />
        </div>
        {m.due.length === 0 && <div style={Object.assign({}, S.row(DUE_GRID), { color: INK_3 })}><div /><div>Nothing to file in the next 45 days.</div></div>}
        {m.due.map(function(ln) { return dueLine(ln, false); })}
        {m.upcoming.count > 0 && (
          <details style={{ borderBottom: '1px solid ' + LINE_CONTAINER }} data-testid="tax-upcoming">
            <summary style={{ listStyle: 'none', cursor: 'pointer', display: 'grid', gridTemplateColumns: DUE_GRID, gap: '0 16px', alignItems: 'center', minHeight: 52, padding: '10px 0' }}>
              <div style={S.mute}>Later</div>
              <div style={S.ink2}>Upcoming <span style={S.mute}>· {m.upcoming.count} return{m.upcoming.count === 1 ? '' : 's'}{m.upcoming.range ? ', ' + m.upcoming.range : ''}</span></div>
              <div style={Object.assign({}, S.num, S.mute)}>{fmtMoney(m.upcoming.amount)} <span>so far</span></div>
              <div style={S.mute}>accumulating</div>
              <div style={Object.assign({}, S.mute, { textAlign: 'right' })}>Show</div>
            </summary>
            <div style={{ borderTop: '1px solid ' + LINE_ROW }}>
              {m.upcoming.lines.map(function(ln) { return dueLine(ln, true); })}
            </div>
          </details>
        )}
        {footnotes()}
      </div>
    );
  }

  // ── View 2: Filed.
  function viewFiled() {
    const hdr = Object.assign({}, S.lbl, { display: 'grid', gridTemplateColumns: FILED_GRID, gap: '0 14px', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid ' + LINE_CONTAINER });
    // Every cell min-width 0; Return/Confirmation/PDF clip with an ellipsis
    // (full value in title) so no cell can ever print over its neighbour.
    const C = { minWidth: 0 };
    const CLIP = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
    return (
      <div data-testid="tax-view-filed">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <label style={S.lbl} htmlFor="st-year">Year</label>
          <select id="st-year" value={year} onChange={function(e) { setYear(e.target.value); }} style={S.select}>{years.map(function(y) { return <option key={y} value={y}>{y}</option>; })}</select>
          <label style={Object.assign({}, S.lbl, { marginLeft: 8 })} htmlFor="st-return">Return</label>
          <select id="st-return" value={returnFilter} onChange={function(e) { setReturnFilter(e.target.value); }} style={S.select}>
            <option value="">All returns</option>
            {returnOptions.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 1040 }}>
            <div style={hdr}>
              <span /><span>Filed</span><span>Return · period</span><span style={S.num}>Paid to state</span><span style={S.num}>Items stamped</span><span>Confirmation</span><span>PDF</span><span>Note</span><span />
            </div>
            {filed.rows.length === 0 && <div style={Object.assign({}, S.row(FILED_GRID), { color: INK_3 })}><span /><span /><div>No filings recorded for {year}.</div></div>}
            {filed.rows.map(function(r) {
              const f = r.filing;
              let note = null;
              if (r.note) {
                if (r.note.kind === 'half') note = <span style={S.ink2}>Half recorded · <button type="button" onClick={function() { editFiling(f); }} style={S.lnk2}>complete it</button></span>;
                else if (r.note.kind === 'short') note = <span style={{ color: ZONE_FUCHSIA }}>{r.note.text}</span>;
                else if (r.note.kind === 'rounding') note = <span style={S.mute}>{r.note.text}</span>;
                else note = <span style={S.ink2}>{r.note.text}</span>;
              }
              return (
                <div key={f.id} style={Object.assign(S.row(FILED_GRID), { gap: '0 14px' })} data-testid={'filed-row-' + f.id}>
                  <Dot state={r.half ? 'half' : 'filed'} />
                  <div style={C}>{salesTaxShortDate(r.filedDate)}</div>
                  <div style={CLIP} title={r.returnLabel + ' · ' + r.periodLabel}>
                    {r.returnLabel}
                    <span style={S.mute}> · {r.periodLabel}</span>
                    {r.zero && <span style={S.mute}> · zero return</span>}
                    {r.firstReturn && <span style={S.mute}> · first return</span>}
                  </div>
                  <div style={Object.assign({}, C, S.num)}>{fmtMoney(r.paid)}</div>
                  <div style={Object.assign({}, C, S.num)} data-testid={'filed-stamps-' + f.id}>
                    {r.zero && !r.stampCount ? <span style={S.mute}>0</span> : <span>{r.stampCount}{r.stampCount ? <span style={S.mute}> · {fmtMoney(r.stampSum)}</span> : null}</span>}
                  </div>
                  <div style={CLIP} title={r.confirmationNo || ''}>{r.confirmationNo ? <span style={S.mono}>{r.confirmationNo}</span> : <span style={S.mute}>not entered</span>}</div>
                  <div style={CLIP}>
                    {r.files.length === 0 && <span style={S.mute}>none</span>}
                    {r.files.length === 1 && <button type="button" onClick={function() { openFiles(r.files); }} style={S.lnk2} title={r.files[0].filename || 'Return.pdf'} data-testid={'filed-pdf-' + f.id}>PDF</button>}
                    {r.files.length > 1 && <button type="button" onClick={function() { openFiles(r.files); }} style={S.lnk2} title={r.files.map(function(x) { return x.filename; }).filter(Boolean).join('\n')} data-testid={'filed-pdf-' + f.id}>{r.files.length} PDFs</button>}
                  </div>
                  <div style={Object.assign({}, C, { fontSize: 13, lineHeight: 1.35 })}>{note}</div>
                  <div style={Object.assign({}, C, { textAlign: 'right' })}>{canRecord && <button type="button" onClick={function() { editFiling(f); }} style={S.lnk2}>Edit</button>}</div>
                </div>
              );
            })}
            {filed.rows.length > 0 && (
              <div style={Object.assign(S.row(FILED_GRID), { gap: '0 14px', borderBottom: 0 })}>
                <span /><span />
                <div style={S.lbl}>Remitted · {year}</div>
                <div style={Object.assign({}, S.num, { fontWeight: 500 })} data-testid="filed-total-paid">{fmtMoney(filed.totals.paid)}</div>
                <div style={Object.assign({}, C, S.num, S.mute)} data-testid="filed-total-items">{filed.totals.items} item{filed.totals.items === 1 ? '' : 's'}</div>
              </div>
            )}
          </div>
        </div>
        <p style={{ margin: '24px 0 0', maxWidth: '72ch', fontSize: 13, color: INK_3 }}>
          Items stamped shows the count and the sum of the frozen per-item amounts. A note appears only when the stamped sum and the amount paid differ: rounding under $1 is muted, anything larger names the cause.
        </p>
      </div>
    );
  }

  // ── View 3: Detail by month.
  function viewDetail() {
    const periods = reportYear.periods.map(function(p) {
      return Object.assign({}, p, { states: p.states.filter(function(st) { return (st.registered || st.hasActivity) && (!stateFilter || st.stateCode === stateFilter); }) });
    }).filter(function(p) { return p.states.length; });
    const rolledFor = function(periodKey, stateCode) {
      return (reportYear.rolledForward || []).find(function(r) { return r.periodKey === periodKey && r.stateCode === stateCode; }) || null;
    };
    const th = Object.assign({}, S.lbl, { textAlign: 'right', padding: '8px 10px 8px 0', borderBottom: '1px solid ' + LINE_CONTAINER, whiteSpace: 'nowrap' });
    const td = { padding: '12px 10px 12px 0', borderBottom: '1px solid ' + LINE_ROW, textAlign: 'right', fontFeatureSettings: TABULAR, whiteSpace: 'nowrap', verticalAlign: 'middle' };
    const tdL = Object.assign({}, td, { textAlign: 'left' });
    return (
      <div data-testid="tax-view-detail">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={S.lbl} htmlFor="st-year-d">Year</label>
            <select id="st-year-d" value={year} onChange={function(e) { setYear(e.target.value); }} style={S.select}>{years.map(function(y) { return <option key={y} value={y}>{y}</option>; })}</select>
            <label style={Object.assign({}, S.lbl, { marginLeft: 8 })} htmlFor="st-state">State</label>
            <select id="st-state" value={stateFilter} onChange={function(e) { setStateFilter(e.target.value); }} style={S.select}>
              <option value="">All states</option>
              {stateOptions.map(function(o) { return <option key={o.code} value={o.code}>{o.name}</option>; })}
            </select>
          </div>
          <button type="button" onClick={exportCsv} style={S.pill(false)} title="One row per final invoice, every column, for the accountants' spreadsheet">↓ CSV</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 960, borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={Object.assign({}, th, { textAlign: 'left' })}>State</th>
                <th style={th}>Gross sales</th><th style={th}>Non-taxable</th><th style={th}>Taxable</th><th style={th}>Rate</th><th style={th}>Tax due</th><th style={th}>Filed</th>
              </tr>
            </thead>
            <tbody>
              {periods.length === 0 && <tr><td colSpan={7} style={Object.assign({}, tdL, { color: INK_3 })}>No registered state and no sales in {year}.</td></tr>}
              {periods.map(function(period) {
                const rows = [];
                let returnsN = 0, filedN = 0, paidSum = 0, dueSum = 0, rolledOnly = true, anyRolled = false;
                const dueDates = [];
                const pstate = salesTaxPeriodState(period.key, '', today);
                period.states.forEach(function(st) {
                  const key = st.periodKey || period.key;
                  const rolled = rolledFor(period.key, st.stateCode);
                  const rets = salesTaxRowReturns(st);
                  const multi = rets.length > 1;
                  rets.forEach(function(ret, ri) {
                    const own = salesTaxFilingsFor(salesTaxFilings, st.stateCode, key, ret.key);
                    const useRolled = rolled && !st.hasActivity && ri === 0;
                    // Before this return's registration: show the month only
                    // when goods arrived (rolled onto the first return);
                    // otherwise the state had no return to file yet.
                    const recForReg = salesTaxStateRuleFor(stateTaxRecords, st.stateCode, ret.jurisdiction);
                    if (!useRolled && !own.length && !salesTaxReturnOwedForPeriod(key, recForReg, st)) return;
                    const fig = useRolled ? rolled : { grossSales: st.grossSales, nonTaxable: st.nonTaxable, taxable: st.taxable, tax: multi ? ret.tax : st.tax };
                    const rate = multi ? ret.rate : (useRolled ? rolled.rate : st.rate);
                    const rec = salesTaxStateRuleFor(stateTaxRecords, st.stateCode, ret.jurisdiction);
                    const due = salesTaxDueDate(key, rec ? rec.dueRule : '');
                    const intoFilings = useRolled ? salesTaxFilingsFor(salesTaxFilings, st.stateCode, rolled.intoPeriodKey, ret.key) : [];
                    const isFiled = own.length > 0 || (useRolled && intoFilings.length > 0);
                    returnsN += 1;
                    if (isFiled) filedN += 1; else { dueSum += fig.tax; if (due) dueDates.push(due); }
                    if (!useRolled) rolledOnly = false; else anyRolled = true;
                    paidSum += sumSalesTaxFilings(own);
                    const rowKey = period.key + ':' + st.stateCode + ':' + (ret.key || '');
                    let filedCell;
                    if (useRolled) {
                      const into = salesTaxShortDate(salesTaxPeriodEndDate(rolled.intoPeriodKey)).split(' ')[0];
                      filedCell = intoFilings.length
                        ? <button type="button" onClick={function() { editFiling(intoFilings[0]); }} style={S.lnk}>in {into} return</button>
                        : <span style={S.mute}>files on the {into} return</span>;
                    } else if (own.length) {
                      filedCell = <button type="button" onClick={function() { editFiling(own[0]); }} style={S.lnk} data-testid={'detail-filed-' + rowKey}>{fmtMoney(sumSalesTaxFilings(own))}</button>;
                    } else if (canRecord && pstate !== 'not_started') {
                      filedCell = <button type="button" onClick={function() { openModal(period, st, null, fig.tax < 0.005 ? 3 : 1, multi ? ret : null); }} style={S.lnk} data-testid={'detail-file-' + rowKey}>File &amp; record</button>;
                    } else filedCell = <span style={S.mute}>not yet filed</span>;
                    rows.push(
                      <tr key={rowKey} data-testid={'detail-row-' + rowKey}>
                        <td style={tdL}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                            <Dot state={isFiled ? 'filed' : 'open'} />
                            <span>{st.stateName}{multi && <span style={S.mute}> · {ret.key ? ret.label : 'state'}</span>}{st.periodKey && <span style={S.mute}> · {st.periodLabel.split(' ')[0]}</span>}{useRolled && <span style={S.mute}> · before registration</span>}</span>
                          </span>
                        </td>
                        <td style={td}>{fmtMoney(fig.grossSales)}</td>
                        <td style={td}>{fmtMoney(fig.nonTaxable)}</td>
                        <td style={td}>{fmtMoney(fig.taxable)}</td>
                        <td style={Object.assign({}, td, { color: INK_2 })}>{ratePct(rate)}</td>
                        <td style={td}>{fmtMoney(fig.tax)}</td>
                        <td style={td}>{filedCell}</td>
                      </tr>
                    );
                  });
                });
                let summary;
                const r2 = function(n) { return Math.round(n * 100) / 100; };
                const dueRange = (function() { const ds = dueDates.slice().sort(); if (!ds.length) return ''; const a = salesTaxShortDate(ds[0]), b = salesTaxShortDate(ds[ds.length - 1]); return a === b ? a : a.split(' ')[0] === b.split(' ')[0] ? a + '–' + b.split(' ')[1] : a + ' – ' + b; })();
                if (anyRolled && rolledOnly) summary = 'on the ' + period.states[0].stateName + ' first return';
                else if (filedN === returnsN) summary = returnsN + ' return' + (returnsN === 1 ? '' : 's') + ' · all filed · ' + fmtMoney(r2(paidSum)) + ' paid';
                else if (pstate === 'open' || pstate === 'not_started' || filedN === 0) summary = returnsN + ' return' + (returnsN === 1 ? '' : 's') + ' · ' + fmtMoney(r2(dueSum)) + (dueRange ? ' due ' + dueRange : '');
                else summary = returnsN + ' returns · ' + (returnsN - filedN) + ' to file · ' + fmtMoney(r2(dueSum)) + (dueRange ? ' due ' + dueRange : '');
                return [
                  <tr key={period.key + ':band'} data-testid={'detail-band-' + period.key}>
                    <td colSpan={7} style={{ padding: '36px 0 10px', borderBottom: '1px solid ' + INK_1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
                        <span><span style={{ fontSize: 17, fontWeight: 500, color: INK_1 }}>{period.label}</span>{pstate === 'open' && <span style={{ fontSize: 13, color: INK_2, marginLeft: 12 }}>period open</span>}{pstate === 'not_started' && <span style={{ fontSize: 13, color: INK_2, marginLeft: 12 }}>period not started</span>}</span>
                        <span style={{ fontSize: 13, color: INK_2 }}>{summary}</span>
                      </div>
                    </td>
                  </tr>,
                ].concat(rows);
              })}
            </tbody>
          </table>
        </div>
        <p style={{ margin: '24px 0 0', maxWidth: '72ch', fontSize: 13, color: INK_3 }}>
          Every registered state appears every period, including at $0: a zero return is still a return. Green dot = filed, outline = still to file. The Filed column is the door to the modal: File &amp; record opens a new return at Step 1; a filed amount opens that return&rsquo;s record.
        </p>
      </div>
    );
  }

  function footnotes() {
    const r = reportAll;
    const lines = [];
    if (r.pending.count > 0) lines.push(fmtMoney(r.pending.tax) + ' invoiced but not yet collected on ' + r.pending.count + ' final invoice' + (r.pending.count === 1 ? '' : 's') + ' (' + r.pending.byState.map(function(p) { return p.stateCode + (r.pending.byState.length > 1 ? ' ' + fmtMoney(p.tax) : ''); }).join(', ') + '). Not owed until the money lands.');
    r.exports.forEach(function(ex) { lines.push('Outside the US, not filed here: ' + ex.projects.join(', ') + ' (' + ex.place + '): ' + fmtMoney(ex.invoiced) + ' across ' + ex.count + ' final invoice' + (ex.count === 1 ? '' : 's') + '. Duty and tax are paid at destination by the client’s customs agent.'); });
    if (r.unassigned.length) lines.push(r.unassigned.length + ' paid final invoice' + (r.unassigned.length === 1 ? '' : 's') + ' could not be assigned to a state: ' + r.unassigned.map(function(u) { return u.invoiceRef + ' (' + u.reason + ')'; }).join(', ') + '.');
    if (!lines.length) return null;
    return <p style={{ margin: '28px 0 0', maxWidth: '72ch', fontSize: 13, color: INK_3 }}>{lines.map(function(t, i) { return <span key={i}>{i > 0 && <br />}{t}</span>; })}</p>;
  }

  return (
    <div style={Object.assign({}, S.body, { marginBottom: 32 })} data-testid="sales-tax-section" data-view={view}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ maxWidth: '62ch', minWidth: 0 }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 500, color: INK_1 }}>Sales tax</h2>
          {sentence()}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="tablist">
          {VIEWS.map(function(v) {
            return <button key={v[0]} type="button" role="tab" aria-selected={view === v[0]} onClick={function() { setView(v[0]); }} style={S.seg(view === v[0])} data-testid={'tax-view-' + v[0] + '-tab'}>{v[1]}</button>;
          })}
        </div>
      </div>
      {view === 'due' && viewDue()}
      {view === 'filed' && viewFiled()}
      {view === 'detail' && viewDetail()}
      {modal && (
        <SalesTaxFilingModal
          periods={modal.periods} existing={modal.existing} ret={modal.ret}
          stateCode={modal.stateCode} stateName={modal.stateName} grouping={modal.grouping} initialStep={modal.initialStep}
          onClose={closeModal} />
      )}
    </div>
  );
}
